/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Knowledge Base Studio & Vectorize Pipeline View (Task 10.3.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  useKnowledgeStore,
  type KnowledgeDocumentItem,
  type KnowledgeFilterStatus,
  type KnowledgeFilterType,
} from '../stores/useKnowledgeStore';
import { ChunkPreviewerDrawer } from '../components/knowledge/ChunkPreviewerDrawer';
import { UploadDocumentModal } from '../components/knowledge/UploadDocumentModal';
import { useI18n } from '../hooks/useI18n';
import {
  Database,
  Search,
  Layers,
  FileText,
  FileCode,
  FileSpreadsheet,
  File,
  HardDrive,
  RefreshCw,
  Trash2,
  Eye,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Server,
  Plus,
} from 'lucide-react';

export const KnowledgeBaseView: React.FC = () => {
  const { t, locale } = useI18n();
  const {
    documents,
    searchQuery,
    filterStatus,
    filterType,
    activeReindexingId,
    reindexProgress,
    setSearchQuery,
    setFilterStatus,
    setFilterType,
    deleteDocument,
    reindexDocument,
    openChunkPreview,
    openUploadModal,
    getFilteredDocuments,
    getTotalStorageBytes,
    getTotalChunksCount,
  } = useKnowledgeStore();

  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState<string | null>(null);

  const filteredDocs = getFilteredDocuments();
  const totalStorageBytes = getTotalStorageBytes();
  const totalChunks = getTotalChunksCount();
  const activeCount = documents.filter((d) => d.status === 'active').length;

  const formatStorage = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getDocIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return FileText;
      case 'docx':
        return File;
      case 'csv':
        return FileSpreadsheet;
      case 'markdown':
        return FileCode;
      default:
        return FileText;
    }
  };

  const getStatusBadge = (doc: KnowledgeDocumentItem) => {
    const isThisReindexing = activeReindexingId === doc.id;

    if (isThisReindexing) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-bold font-mono">
          <RefreshCw className="w-3 h-3 animate-spin text-primary" />
          {t('knowledge.reindexingStatus', { progress: reindexProgress })}
        </span>
      );
    }

    switch (doc.status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.8 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[11px] font-semibold">
            <CheckCircle2 className="w-3 h-3" />
            {t('knowledge.activeStatus')}
          </span>
        );
      case 'vectorizing':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.8 rounded-full bg-accent/10 text-accent border border-accent/20 text-[11px] font-semibold">
            <RefreshCw className="w-3 h-3 animate-spin" />
            {t('knowledge.vectorizingStatus')}
          </span>
        );
      case 'chunking':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.8 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-semibold">
            <Layers className="w-3 h-3" />
            {t('knowledge.chunkingStatus')}
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.8 rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20 text-[11px] font-semibold">
            <AlertTriangle className="w-3 h-3" />
            {t('knowledge.failedStatus')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.8 rounded-full bg-muted text-foreground-muted text-[11px]">
            <Clock className="w-3 h-3" />
            {t('knowledge.uploadingStatus')}
          </span>
        );
    }
  };

  const statusOptions: { label: string; value: KnowledgeFilterStatus }[] = [
    { label: t('knowledge.allStatuses'), value: 'all' },
    { label: t('knowledge.activeStatus'), value: 'active' },
    { label: t('knowledge.vectorizingStatus'), value: 'vectorizing' },
    { label: t('knowledge.chunkingStatus'), value: 'chunking' },
    { label: t('knowledge.failedStatus'), value: 'failed' },
  ];

  const typeOptions: { label: string; value: KnowledgeFilterType }[] = [
    { label: t('knowledge.allFormats'), value: 'all' },
    { label: 'PDF', value: 'pdf' },
    { label: 'DOCX', value: 'docx' },
    { label: 'Markdown', value: 'markdown' },
    { label: 'CSV', value: 'csv' },
    { label: 'TXT', value: 'txt' },
  ];

  return (
    <div className="space-y-6">
      {/* 1. KPI & System Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric 1: R2 Storage */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">{t('knowledge.storageR2Metric')}</span>
            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <HardDrive className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <h3 className="font-heading font-extrabold text-xl text-foreground">
              {formatStorage(totalStorageBytes)}
            </h3>
            <p className="text-[10px] text-foreground-muted font-mono">
              {t('knowledge.zeroEgressNote')}
            </p>
          </div>
        </div>

        {/* Metric 2: Vector Chunks */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">{t('knowledge.totalChunksMetric')}</span>
            <div className="w-8 h-8 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <h3 className="font-heading font-extrabold text-xl text-foreground">
              {t('knowledge.chunksCount', { count: totalChunks })}
            </h3>
            <p className="text-[10px] text-foreground-muted font-mono">
              {t('knowledge.dimsNote')}
            </p>
          </div>
        </div>

        {/* Metric 3: Active Documents */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">{t('knowledge.activeDocsMetric')}</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <h3 className="font-heading font-extrabold text-xl text-foreground">
              {activeCount} / {documents.length}
            </h3>
            <p className="text-[10px] text-foreground-muted">
              {t('knowledge.readyForChatAndSearch')}
            </p>
          </div>
        </div>

        {/* Metric 4: Vectorize Engine Status */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground-muted">{t('knowledge.vectorizeIndexMetric')}</span>
            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Server className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-0.5">
            <h3 className="font-heading font-extrabold text-xl text-foreground flex items-center gap-1.5">
              <span>{t('knowledge.optimalStatus')}</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </h3>
            <p className="text-[10px] text-foreground-muted font-mono truncate">
              sophia-knowledge-1536 (Edge)
            </p>
          </div>
        </div>
      </div>

      {/* 2. Search & Filter Bar with Action Buttons */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-2xl border border-surface-border bg-surface shadow-sm">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('knowledge.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-1 focus:ring-primary shadow-inner"
          />
        </div>

        {/* Filters and Upload Action */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as KnowledgeFilterStatus)}
            className="px-3 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as KnowledgeFilterType)}
            className="px-3 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Upload Button */}
          <button
            type="button"
            onClick={openUploadModal}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-fg hover:opacity-95 text-xs font-bold shadow-sm transition-opacity"
          >
            <Plus className="w-4 h-4" />
            <span>{t('knowledge.uploadNewBtn')}</span>
          </button>
        </div>
      </div>

      {/* 3. Documents Table */}
      <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-surface-elevated/60 text-[11px] font-bold uppercase tracking-wider text-foreground-muted border-b border-surface-border">
              <tr>
                <th className="py-3.5 px-4">{t('knowledge.tableDocAndFormat')}</th>
                <th className="py-3.5 px-4">{t('knowledge.tableStatus')}</th>
                <th className="py-3.5 px-4">{t('knowledge.tableChunksAndVectors')}</th>
                <th className="py-3.5 px-4 hidden md:table-cell">{t('knowledge.tableR2Storage')}</th>
                <th className="py-3.5 px-4 hidden lg:table-cell">{t('knowledge.tableUpdated')}</th>
                <th className="py-3.5 px-4 text-right">{t('knowledge.tableActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {filteredDocs.length > 0 ? (
                filteredDocs.map((doc) => {
                  const DocIcon = getDocIcon(doc.fileType);
                  const isReindexing = activeReindexingId === doc.id;

                  return (
                    <tr
                      key={doc.id}
                      className="hover:bg-surface-elevated/40 transition-colors group"
                    >
                      {/* Document Name & Type */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                            <DocIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-heading font-bold text-xs text-foreground truncate max-w-[220px] sm:max-w-xs">
                              {doc.title}
                            </h4>
                            <p className="text-[10px] text-foreground-muted font-mono truncate max-w-[200px]">
                              {doc.fileName} • {doc.formattedSize}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 whitespace-nowrap">{getStatusBadge(doc)}</td>

                      {/* Chunks & Dimensions */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="space-y-0.5">
                          <span className="font-semibold text-foreground font-mono">
                            {t('knowledge.chunksCount', { count: doc.chunkCount })}
                          </span>
                          <span className="text-[10px] text-foreground-muted block font-mono">
                            {doc.embeddingDimensions}d • {doc.embeddingModel.split('/').pop()}
                          </span>
                        </div>
                      </td>

                      {/* R2 Key */}
                      <td className="py-3.5 px-4 hidden md:table-cell">
                        <span className="font-mono text-[10px] text-foreground-muted truncate max-w-[180px] block" title={doc.r2StorageKey}>
                          {doc.r2StorageKey}
                        </span>
                      </td>

                      {/* Updated Date */}
                      <td className="py-3.5 px-4 hidden lg:table-cell whitespace-nowrap text-foreground-muted text-[11px]">
                        {new Date(doc.updatedAt).toLocaleDateString(locale === 'vi' ? 'vi-VN' : 'en-US')}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Chunk Previewer */}
                          <button
                            type="button"
                            onClick={() => openChunkPreview(doc)}
                            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-muted text-foreground-muted hover:text-foreground transition-colors"
                            title={t('knowledge.previewChunksTooltip')}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>

                          {/* Re-index Vector */}
                          <button
                            type="button"
                            onClick={() => reindexDocument(doc.id)}
                            disabled={isReindexing}
                            className={`p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-muted transition-colors ${
                              isReindexing
                                ? 'text-primary border-primary/40'
                                : 'text-foreground-muted hover:text-foreground'
                            }`}
                            title={t('knowledge.reindexTooltip')}
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${isReindexing ? 'animate-spin' : ''}`} />
                          </button>

                          {/* Delete Document */}
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteDocId(doc.id)}
                            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-rose-500/10 text-foreground-muted hover:text-rose-500 transition-colors"
                            title={t('knowledge.deleteDocTooltip')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-foreground-muted space-y-2">
                    <Database className="w-8 h-8 mx-auto text-foreground-muted/40" />
                    <p className="text-xs">{t('knowledge.noDocsFound')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Delete Confirmation Modal Dialog */}
      {confirmDeleteDocId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setConfirmDeleteDocId(null)}
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl border border-surface-border bg-surface p-5 shadow-2xl space-y-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 text-destructive">
              <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-heading font-bold text-sm text-foreground">
                  {t('knowledge.deleteModalTitle')}
                </h4>
                <p className="text-[11px] text-foreground-muted">
                  {t('knowledge.deleteModalConfirm', { title: documents.find((d) => d.id === confirmDeleteDocId)?.title || '' })}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setConfirmDeleteDocId(null)}
                className="px-3 py-1.5 rounded-xl border border-surface-border text-xs font-semibold hover:bg-muted text-foreground-muted hover:text-foreground transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteDocument(confirmDeleteDocId);
                  setConfirmDeleteDocId(null);
                }}
                className="px-3 py-1.5 rounded-xl bg-destructive text-destructive-fg hover:opacity-90 text-xs font-semibold transition-opacity shadow-sm"
              >
                {t('knowledge.deletePermanentlyBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. Chunk Previewer Drawer */}
      <ChunkPreviewerDrawer />

      {/* 6. Upload Document Modal */}
      <UploadDocumentModal />
    </div>
  );
};
